import Fuse from 'fuse.js'

import { LayingPatternViewer } from 'CommonScripts/layingPatternViewer';
import { isInFrame } from 'CommonScripts/frameHelper';
import { CatalogueContext } from 'CommonScripts/catalogueContext';

import { VisualisationGatewayService } from 'CommonData/services/visualisationGateway.service';
import { LayingPattern, ProductTexture, JointTexture, Filter, ProductModel, CategoryType, ProductSynopses } from 'CommonData/poco/index';
import { ProductTextureMapper, ProductModelMapper, FilterMapper, CategoryTypesMapper, ProductSynopsesMapper } from 'CommonData/mappers/index';

import { NavHelper, NavTypes } from './nav';
import { ColourMixing } from './colourMixing';
import { ProductDescription } from './productDescription';
import { InputHelper } from 'CommonScripts/inputHelper';
import { SplashScreen } from './splashScreen';
import { LoadingHelper } from 'CommonScripts/loadingHelper';
import { Clone } from 'CommonScripts/clone';
import { DomesticBasket } from './domesticBasket';
import { CommercialCTA } from './commercialCTA';
import { VirtualPlatforms } from './virtualPlatforms';
import { Tools } from './tools';
import { AppInteraction } from 'CommonScripts/appInteraction';
import { ResolutionOption } from './resolution';

export { baseProductTextures, matchingProductTexture, currentLayingPatternId, currentJointingKey, productSynopses, getCurrentProductSynopses, updateProductName };

const visualiserEl: Element = document.getElementsByClassName('js-visualiser')[0];
const visualiserLoadingEl: Element = document.getElementsByClassName('js-visualiser-loading')[0];
const textureHeaderEl: Element = document.getElementsByClassName('js-product-texture-header')[0];
const patternHeaderEl: Element = document.getElementsByClassName('js-pattern-header')[0];

const layingPatternListEl: Element = document.getElementsByClassName('js-laying-pattern-list')[0];
const baseLayingPatternEl: Element = document.getElementsByClassName('js-laying-pattern-base')[0];
let layingPatternEls: HTMLCollectionOf<Element> = null;

const jointingListEl: Element = document.getElementsByClassName('js-jointing-list')[0];
const baseJointingEl: Element = document.getElementsByClassName('js-jointing-base')[0];
let jointingEls: HTMLCollectionOf<Element> = null;

const productListEl: Element = document.getElementsByClassName('js-product-list')[0];
const subProductListEl: Element = document.getElementsByClassName('js-sub-product-list')[0];
let baseProductEl: Element = null;
let productEls: HTMLCollectionOf<Element> = null;
const productCountEls: HTMLCollectionOf<Element> = document.getElementsByClassName('js-product-count');
const productNameEl: Element = document.getElementsByClassName('js-product-name')[0];
const productPatternEl: Element = document.getElementsByClassName('js-product-pattern')[0];
const productResEl: Element = document.getElementsByClassName('js-product-res')[0];
const productColourEl: Element = document.getElementsByClassName('js-product-colour')[0];
const productColourMixEl: Element = document.getElementsByClassName('js-product-colour-mix')[0];
const productNameContainerEl: Element = document.getElementsByClassName('js-product-name-container')[0];

const filterSelectionListEl: Element = document.getElementsByClassName('js-filter-selection')[0];
const baseFilterEl: Element = document.getElementsByClassName('js-filter-base')[0];
let filterEls: HTMLCollectionOf<Element> = null;
const clearFiltersEl: Element = document.getElementsByClassName('js-clear-filters')[0];
const openFiltersMenuEl: Element = document.getElementsByClassName('js-filter-menu')[0];
const filterBackEl: Element = document.getElementsByClassName('js-filter-back-button')[0];
const searchInputEl: HTMLInputElement = document.getElementsByClassName('js-search-input')[0] as HTMLInputElement;
const patternBackEl: Element = document.getElementsByClassName('js-products-back-button')[0];
const productCloseEls: HTMLCollectionOf<Element> = document.getElementsByClassName('js-product-close-button');

const categoryTypeListEl: Element = document.getElementsByClassName('js-category-list')[0];
const baseCategoryTypeItemEl: Element = document.getElementsByClassName('js-category-item')[0];

const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
const textureKey: string = urlParams.get('productkey');
const patternId: string = urlParams.get('patternid');
const productId: string = urlParams.get('productid');
const jointKey: string = urlParams.get('jointkey');
const modelUrl: string = urlParams.get('modelurl');
const includeDayCycle: string = urlParams.get('includedaycycle');
const isShareLink: boolean = !!urlParams.get('share');
const enableTools: boolean = !!urlParams.get('tools');
const productCategoryKey: string = urlParams.get('productcategorykey');

let previousMatchingProductTexture: ProductTexture = null;
let matchingProductTexture: ProductTexture = null;
let currentLayingPatternId: number = null;
let currentJointingKey: string = null;
let currentCategoryType: CategoryType = null;
let isSubsetProducts = false;

const getCurrentProductSynopses = () => {
    const baseTextureKey: string = LayingPatternViewer.getCurrentTexture().ProductTextureKey;
    let product: ProductSynopses = productSynopses?.find((ps: ProductSynopses) => ps.id === baseProductTextures.find((pt: ProductTexture) => baseTextureKey === pt.textureKey)?.productId);
    if (!product) {
        product = productSynopses?.find((ps: ProductSynopses) => ps.id === baseProductTextures.find((pt: ProductTexture) => pt.textureKey.includes(baseTextureKey.split('|')[0]))?.productId);
    }

    return product;
};

const setProductCountText = (count: number = null) => {
    [].slice.call(productCountEls).forEach((pcEl: Element) => {
        pcEl.innerHTML = `${count ?? productTextures.length}`;
    });

    const modelCountEl: Element = document.getElementsByClassName('js-model-count')[0];
    if (modelCountEl) {
        modelCountEl.innerHTML = `[${modelTextures.length}]`;
    }
};

const setCategoryTypesEls = () => {
    categoryTypeListEl.innerHTML = '';
    categoryTypes.forEach((ct: CategoryType) => {
        const el: HTMLElement = baseCategoryTypeItemEl.cloneNode(true) as HTMLElement;

        const span: HTMLSpanElement = document.createElement('span');
        span.innerHTML = ct.name;

        el.appendChild(span);
        el.setAttribute('data-category', ct.key);

        el.addEventListener('click', () => { 
            applyCategoryType(ct.key);
        });

        categoryTypeListEl.appendChild(el);
    });
};

const applyCategoryType = (typeKey: string) => {
    const categoryTypeElsArray: Array<Element> = [].slice.call(categoryTypeListEl.children);
    const matchingCategoryTypeEl: Element = categoryTypeElsArray.find((ctEl: Element) => ctEl.getAttribute('data-category') === typeKey);
    currentCategoryType = categoryTypes.find((ct: CategoryType) => ct.key === typeKey);

    categoryTypeElsArray.forEach((ctEl: Element) => {
        ctEl.classList.remove('selected');
    });

    matchingCategoryTypeEl.classList.add('selected');

    if (currentCategoryType.isModelCategory) {
        openFiltersMenuEl.classList.add('invisible');
        modelTextures = modelTextures.filter((mt: ProductModel) => mt.categoryType === getSelectedCategoryType());

        setModelsEls(productListEl, modelTextures);
        setProductCountText(modelTextures ? modelTextures.length : 0);
    } else {
        openFiltersMenuEl.classList.remove('invisible');
        clearAllFilters();

        // Hide all currently disabled filter elements after you select a category type.
        [].slice.call(document.getElementsByClassName('js-filter')).forEach((fEl: Element) => {
            fEl.classList.remove('hidden');

            if (fEl.classList.contains('disabled')) {
                fEl.classList.add('hidden');
            }
        });
    }
};

const getSelectedCategoryType = () => [].slice.call(document.getElementsByClassName('js-category-item')).find((ctEl: Element) => ctEl.classList.contains('selected')).getAttribute('data-category');

const setFiltersEls = () => {
    filterSelectionListEl.innerHTML = '';
    filters.forEach((f: Filter) => {
        const el: Element = baseFilterEl.cloneNode(true) as HTMLElement;
        const filterDescriptionEl: Element = el.getElementsByClassName('js-filter-description')[0];

        filterDescriptionEl.innerHTML = f.description;
        el.classList.remove('js-filter-base');

        const filterValuesListEl = el.getElementsByClassName('js-filter-values')[0];
        const filterValueEl = filterValuesListEl.getElementsByClassName('js-filter-value-base')[0];

        filterValuesListEl.innerHTML = '';
        f.values.forEach((fv: any) => {
            const vEl = filterValueEl.cloneNode(true) as HTMLElement;

            if (fv.metaKey && fv.metaValue) {
                switch (fv.metaKey) {
                    case 'hex':
                        vEl.classList.remove('rounded');
                        el.classList.add('extended');

                        const colour = document.createElement('div');
                        colour.classList.add('filter-colour');
                        colour.style.backgroundColor = fv.metaValue;
                        vEl.appendChild(colour);

                        break;
                }
            }

            const value: HTMLSpanElement = document.createElement('span');
            value.innerHTML = fv.value;
            vEl.appendChild(value);

            vEl.setAttribute('data-key', `${f.key}`);
            vEl.setAttribute('data-value', `${fv.value}`);

            filterValuesListEl.appendChild(vEl);
        });

        filterSelectionListEl.appendChild(el);
    });

    filterEls = document.getElementsByClassName('js-filter');
    setFilterEventListeners();
};

const toggleFilter = (el: Element) => {
    const isFilterSelected = !el.classList.contains('selected');
    if (!isFilterSelected) {
        el.classList.remove('selected');
    } else {
        el.classList.add('selected');
    }
};

const clearAllFilters = () => {
    [].slice.call(filterEls).forEach((el: Element) => {
        el.classList.remove('selected');
    });

    applyFilters();
};

const applyFilters = () => {
    const filterElsArray: Array<Element> = [].slice.call(filterEls);
    const selectedFilters: Array<any> = filterElsArray.filter((el: Element) => el.classList.contains('selected')).map((el: Element): any => ({ key: el.getAttribute('data-key'), value: el.getAttribute('data-value')}));

    // Clear the searchInputEl as the filter search will override this.
    searchInputEl.value = '';

    productTextures = selectedFilters.length ? baseProductTextures.filter((pt: ProductTexture) => {
        if (pt.filters && pt.filters[0]) {
            let containsAllSelectedFilters: boolean = true;
            selectedFilters.forEach((sf: any) => {
                pt.filters.forEach((ptf: Filter) => {
                    if (ptf.key == sf.key) {
                        if (ptf.values.length > 1) {
                            containsAllSelectedFilters = !!ptf.values.find((ptfv) => ptfv.value == sf.value);
                        } else {
                            ptf.values.forEach((ptfv: any) => {
                                if (ptfv.value != sf.value) {
                                    containsAllSelectedFilters = false;
                                }
                            });
                        }
                    }
                });
            });

            return containsAllSelectedFilters;
        }

        return false;
    }) : baseProductTextures;

    productTextures = productTextures.filter((pt: ProductTexture) => pt.categoryType === getSelectedCategoryType());

    setProductCountText();

    const enabledFilters: Array<Filter> = productTextures.map((pt: ProductTexture) => pt.filters).flat();

    filterElsArray.forEach((el: Element) => {
        const filterKey: string = el.getAttribute('data-key');
        const filterValue: string = el.getAttribute('data-value');
        const isSelected: boolean = el.classList.contains('selected');

        const includesFilter: boolean = !!enabledFilters.find((f: Filter) => f.key === filterKey && f.values.find((fv: any) => fv.value == filterValue));
        const isFilterKeySelected: boolean = selectedFilters.find((sf: any) => sf.key == filterKey);

        if ((!includesFilter || isFilterKeySelected) && !isSelected) {
            el.classList.add('disabled');
        } else {
            el.classList.remove('disabled');
        }
    });

    setProductsEls();
};

const setProductsEls = (targetEl: Element = productListEl, products?: Array<ProductTexture>, onlyShowColour: boolean = false) => {
    products = products ?? productTextures;
    baseProductEl = targetEl.getElementsByClassName('js-product-base')[0] || baseProductEl;
    targetEl.innerHTML = '';

    products.forEach((p: ProductTexture) => {
        const el: HTMLElement = baseProductEl.cloneNode(true) as HTMLElement;

        el.innerHTML = '';
        el.classList.remove('selected');
        el.setAttribute('data-key', `${p.textureKey}`);
        
        const img: HTMLImageElement = document.createElement('img');
        img.setAttribute('src', p.hasMaterialTextureKey && p.hasDiffuseMaterialSwatchImageUrl ? p.materialSwatchImageUrl : p.skuSwatchImageUrl);
        img.setAttribute('alt', p.name);
        img.setAttribute('loading', 'lazy');

        const name: HTMLSpanElement = document.createElement('span');
        if (onlyShowColour) {
            name.innerHTML = p.colour;
        } else {
            name.innerHTML = p.name;
        }

        let disabledSpan: HTMLSpanElement = null;
        if (!p.isValidProductTexture()) {
            el.classList.add('disabled');
            disabledSpan = document.createElement('span');
            disabledSpan.innerHTML = 'Not Available to Visualise';
            disabledSpan.classList.add('disabled-text');
        }

        let mixDiv: HTMLDivElement = null;
        if (p.hasMixedColours) {
            mixDiv = document.createElement('div');
            mixDiv.classList.add('mix-icon', 'js-mix-icon');

            const mixIcon: HTMLDivElement = document.createElement('div');
            mixIcon.classList.add('material-symbols-outlined');
            mixIcon.innerHTML = 'tune';

            const mixSpan: HTMLSpanElement = document.createElement('span');
            mixSpan.innerHTML = 'MIX';

            mixDiv.appendChild(mixIcon);
            mixDiv.appendChild(mixSpan);
        }

        const infoDiv: HTMLDivElement = document.createElement('div');
        infoDiv.classList.add('info-icon', 'js-info-icon');
        infoDiv.setAttribute('data-product-id', p.productId);

        const infoIcon: HTMLDivElement = document.createElement('div');
        infoIcon.classList.add('material-symbols-outlined');
        infoIcon.innerHTML = 'info';
        
        infoDiv.appendChild(infoIcon);

        // Google analytic class name
        // el.classList.add(``);

        el.appendChild(img);
        el.appendChild(name);
        el.appendChild(infoDiv);
        if (disabledSpan) {
            el.appendChild(disabledSpan);
        }
        if (mixDiv) {
            el.appendChild(mixDiv);
        }

        targetEl.appendChild(el);
    });

    productEls = targetEl.getElementsByClassName('js-product');
    setProductEventListeners();
};

const setModelsEls = (targetEl: Element = productListEl, models?: Array<ProductModel>) => {
    models = models ?? modelTextures;
    baseProductEl = targetEl.getElementsByClassName('js-product-base')[0] || baseProductEl;
    targetEl.innerHTML = '';

    models.forEach((m: ProductModel) => {
        const el: HTMLElement = baseProductEl.cloneNode(true) as HTMLElement;

        el.innerHTML = '';
        el.classList.remove('selected');
        el.setAttribute('data-url', `${m.modelUrl}`);
        
        const img: HTMLImageElement = document.createElement('img');
        img.setAttribute('src', m.swatchUrl);

        const name: HTMLSpanElement = document.createElement('span');
        name.innerHTML = m.name;

        el.appendChild(img);
        el.appendChild(name);

        targetEl.appendChild(el);
    });

    productEls = targetEl.getElementsByClassName('js-product');
    setProductModelListeners();
}

const setLayingPatternEls = () => {
    layingPatternListEl.innerHTML = '';
    matchingProductTexture.layingPatterns.forEach((lp: LayingPattern) => {
        const el: HTMLElement = baseLayingPatternEl.cloneNode(true) as HTMLElement;

        el.innerHTML = '';
        el.classList.remove('js-laying-pattern-base');
        el.setAttribute('data-id', `${lp.id}`);

        const id: HTMLSpanElement = document.createElement('span');
        id.classList.add('laying-pattern-id');
        id.innerHTML = `Pattern: ${lp.id}`;
        const img: HTMLImageElement = document.createElement('img');
        img.setAttribute('src', lp.swatchImageUrl);
        const name: HTMLSpanElement = document.createElement('span');
        name.innerHTML = lp.name;

        // Google analytic class name
        el.classList.add(`ga-3d-ptn-btn-${lp.id}`);

        el.appendChild(id);
        el.appendChild(img);
        el.appendChild(name);

        layingPatternListEl.appendChild(el);
    });

    layingPatternEls = document.getElementsByClassName('js-laying-pattern');
    setLayingPatternEventListeners();
};

const getJointTextures = (productTexture: ProductTexture = matchingProductTexture) => {
    let jointTextures: Array<JointTexture> = [];

    if (productTexture.rigidJointTextures && Array.isArray(productTexture.rigidJointTextures)) {
        jointTextures.push(...productTexture.rigidJointTextures);
    }

    if (productTexture.flexibleJointTextures && Array.isArray(productTexture.flexibleJointTextures)) {
        jointTextures.push(...productTexture.flexibleJointTextures);
    }

    return jointTextures;
};

const setJointingEls = () => {
    let jointTextures: Array<JointTexture> = getJointTextures();

    jointingListEl.innerHTML = '';
    jointTextures.forEach((jt: JointTexture) => {
        const el: HTMLElement = baseJointingEl.cloneNode(true) as HTMLElement;

        el.innerHTML = '';
        el.classList.remove('js-jointing-base');
        el.setAttribute('data-key', `${jt.textureKey}`);

        const img: HTMLImageElement = document.createElement('img');
        img.setAttribute('src', jt.swatchImageUrl);
        const name: HTMLSpanElement = document.createElement('span');
        name.innerHTML = jt.name;

        // Google analytic class name
        el.classList.add(`ga-3d-jnt-btn-${jt.textureKey}`);

        el.appendChild(img);
        el.appendChild(name);

        jointingListEl.appendChild(el);
    });

    jointingEls = document.getElementsByClassName('js-jointing');
    setJointingEventListeners();
};

const applySelectedModel = async (productEl: Element) => {
    try {
        LoadingHelper.start();

        const modelUrl: string = productEl.getAttribute('data-url');
        await LayingPatternViewer.updateBaseModel(modelUrl);
    } finally {
        LoadingHelper.stop();
    }
};

const applySelectedProduct = async (productEl: Element, isInitialSelection: boolean = false) => {
    const productTextureKey: string = productEl.getAttribute('data-key');
    matchingProductTexture = baseProductTextures.find((pt: ProductTexture) => pt.textureKey == productTextureKey);

    const allProductEls: HTMLCollectionOf<Element> = document.getElementsByClassName('js-product');
    [].slice.call(allProductEls).forEach((pEl: Element) => {
        pEl.classList.remove('selected');

        if (matchingProductTexture && pEl.getAttribute('data-key') === matchingProductTexture.textureKey) {
            pEl.classList.add('selected');
        }
    });

    if (!isInitialSelection) {
        const areLayingPatternsSame: boolean = previousMatchingProductTexture ? previousMatchingProductTexture.layingPatterns.join('') === matchingProductTexture.layingPatterns.join('') : false;
        const areJointingSame: boolean = previousMatchingProductTexture ? getJointTextures(previousMatchingProductTexture).join('') === getJointTextures(matchingProductTexture).join('') : false;

        try {
            LoadingHelper.start();

            const canResetLayingPatterns: boolean = !areLayingPatternsSame && !!previousMatchingProductTexture;
            const canResetJointing: boolean = !areJointingSame && !!previousMatchingProductTexture;

            if (canResetLayingPatterns) {
                setLayingPatternEls();
            }

            if (canResetJointing) {
                setJointingEls();
            }

            if (!isSubsetProducts) {
                textureHeaderEl.innerHTML = matchingProductTexture.productName;

                if (NavHelper.currentNav !== NavTypes.Pattern) {
                    setProductsEls(subProductListEl, baseProductTextures.filter((pt: ProductTexture) => pt.productId === matchingProductTexture.productId), true);
                    setLayingPatternEls();
                    setJointingEls();
                    applySelectedProduct([].slice.call(productEls).find((pEl: Element) => pEl.getAttribute('data-key') === matchingProductTexture.textureKey), true);
                    applySelectedJointing(jointingEls[0], true);
                    applySelectedLayingPattern(layingPatternEls[0], true);
                    await LayingPatternViewer.updateTexture({ productTexture: matchingProductTexture });
                } else {
                    applySelectedProduct([].slice.call(productEls).find((pEl: Element) => pEl.getAttribute('data-key') === matchingProductTexture.textureKey), true);
                    applySelectedJointing(jointingEls[0], true, true);
                    applySelectedLayingPattern(layingPatternEls[0], true, true);

                    if (canResetLayingPatterns || canResetJointing) {
                        await LayingPatternViewer.updateTexture({ productTexture: matchingProductTexture, layingPatternId: currentLayingPatternId, jointingKey: currentJointingKey });
                    } else {
                        await LayingPatternViewer.updateProductTexture({ productTexture: matchingProductTexture });
                    }
                }
            } else {
                applySelectedJointing(jointingEls[0], true, true);
                applySelectedLayingPattern(layingPatternEls[0], true, true);

                if (canResetLayingPatterns || canResetJointing) {
                    await LayingPatternViewer.updateTexture({ productTexture: matchingProductTexture, layingPatternId: currentLayingPatternId, jointingKey: currentJointingKey });
                } else {
                    await LayingPatternViewer.updateProductTexture(matchingProductTexture);
                }
            }
        } finally {
            if (matchingProductTexture.hasMixedColours) {
                ColourMixing.setBaseSelectedColour({ productTextures: baseProductTextures });
            } else {
                ColourMixing.closeBox();
            }

            updateProductName();

            NavHelper.selectNavByName(NavTypes.Pattern, true);

            previousMatchingProductTexture = matchingProductTexture;

            VirtualPlatforms.expireCurrentARModel();
            
            if (CatalogueContext.isDomestic() || CatalogueContext.isPOS()) {
                DomesticBasket.render();
            } else {
                if (CatalogueContext.isCommercial()) {
                    CommercialCTA.setCommercialDownloadOptions();
                    CommercialCTA.setCommercialCustomPatternForm();
                }

                AppInteraction.submit();
            }

            LoadingHelper.stop();
        }
    }
};

const applySelectedLayingPattern = async (layingPatternEL: Element, isInitialSelection: boolean = false, useCurrentLayingPatternIfExists: boolean = false) => {
    const layingPatternELsList: Array<Element> = [].slice.call(layingPatternEls);
    layingPatternELsList.forEach((lpEl: Element) => {
        lpEl.classList.remove('selected');
    });

    if (useCurrentLayingPatternIfExists) {
        layingPatternEL = layingPatternELsList.find((lpEl: Element) => parseInt(lpEl.getAttribute('data-id')) === currentLayingPatternId) || layingPatternEL;
    }

    layingPatternEL.classList.add('selected');

    const layingPatternId: number = parseInt(layingPatternEL.getAttribute('data-id'));
    currentLayingPatternId = layingPatternId;

    if (!isInitialSelection) {
        try {
            LoadingHelper.start();
            VirtualPlatforms.expireCurrentARModel();

            return await LayingPatternViewer.updatePattern(layingPatternId);
        } finally {
            LoadingHelper.stop();

            updateProductName();
            
            if (CatalogueContext.isDomestic() || CatalogueContext.isPOS()) {
                DomesticBasket.render();
            } else {
                AppInteraction.submit();
            }
        }
    }
};

const applySelectedJointing = async (jointingEl: Element, isInitialSelection: boolean = false, useCurrentJointingIfExists: boolean = false) => {
    const jointingELsList: Array<Element> = [].slice.call(jointingEls);
    jointingELsList.forEach((jEl: Element) => {
        jEl.classList.remove('selected');
    });

    if (useCurrentJointingIfExists) {
        jointingEl = jointingELsList.find((jEl: Element) => jEl.getAttribute('data-key') === currentJointingKey) || jointingEl;
    }

    const jointingKey: string = jointingEl.getAttribute('data-key');
    const matchingJointTexture: JointTexture = getJointTextures().find((jt: JointTexture) => jt.textureKey == jointingKey);
    currentJointingKey = jointingKey;

    ColourMixing.setSelectedJointUrl(matchingJointTexture.swatchImageUrl, true);
    jointingEl.classList.add('selected');

    if (!isInitialSelection) {
        if (matchingJointTexture) {
            const jointingWidth = matchingJointTexture.isFlexibleJoint ? matchingProductTexture.flexibleJointWidthMM : matchingProductTexture.rigidJointWidthMM;
            
            try {
                LoadingHelper.start();
                VirtualPlatforms.expireCurrentARModel();

                return await LayingPatternViewer.updateJointing(jointingKey, jointingWidth);
            } finally {
                LoadingHelper.stop();

                if (CatalogueContext.isDomestic() || CatalogueContext.isPOS()) {
                    DomesticBasket.render();
                } else {
                    AppInteraction.submit();
                }
            }
        }
    }
};

const updateProductName = (isCustomMix? :boolean) =>  {
    if (!matchingProductTexture) {
        return;
    }
    
    productNameEl.innerHTML = `${matchingProductTexture.productName} `;
    productPatternEl.innerHTML = `P${currentLayingPatternId}`
    productColourEl.innerHTML = `${matchingProductTexture.colour}`;

    let attributeText: string;
    const currentRes: string = LayingPatternViewer.getCurrentResolution();
    switch (currentRes) {
        case ResolutionOption.Low:
            productResEl.innerHTML = 'low-res';
            attributeText = 'The texture shown is Low Resolution';
            break;
        case ResolutionOption.Medium:
            productResEl.innerHTML = 'med-res';
            attributeText = 'The texture shown is Medium Resolution';
            break;
        case ResolutionOption.High || ResolutionOption.Ultra:
            productResEl.innerHTML = 'hi-res';
            attributeText = 'The texture shown is High Resolution';
            break;
    }

    productPatternEl.setAttribute('title', `Laying Pattern ID ${currentLayingPatternId}`);
    productResEl.setAttribute('title', `Indicates current texture resolution (Low, Medium, High). ${attributeText}`);

    if (isCustomMix) {
        productColourMixEl.classList.remove('hidden');
    } else {
        productColourMixEl.classList.add('hidden');
    }
};

const showProductName = () => {
    if (matchingProductTexture && matchingProductTexture.name) {
        productNameContainerEl.classList.remove('hidden');
    }
}
const hideProductName = () => productNameContainerEl.classList.add('hidden');

const createBaseEventListeners = () => {
    NavHelper.setNavElEvents();

    searchInputEl.addEventListener('keyup', InputHelper.debounce((e: InputEvent) => {
        const target: HTMLInputElement = e.target as HTMLInputElement;
        const threshold: number = 0.5;

        if (currentCategoryType.isModelCategory) {
            const fuse = new Fuse(modelTextures, {
                keys: ['name', 'filters.values.value'],
                threshold
            });
            
            let newModelTextures: Array<ProductModel> = fuse.search(target.value).map((fi: any) => fi.item);
            if (!newModelTextures.length && !target.value) {
                newModelTextures = modelTextures;
            }

            setModelsEls(productListEl, newModelTextures);
            setProductCountText(newModelTextures.length);
        } else {
            const fuse = new Fuse(productTextures, {
                keys: ['textureKey', 'productId', 'productName', 'name', 'filters.values.value'],
                threshold
            });
            
            let newProductTextures: Array<ProductTexture> = fuse.search(target.value).map((fi: any) => fi.item);
            if (!newProductTextures.length && !target.value) {
                newProductTextures = productTextures;
            }
    
            setProductsEls(productListEl, newProductTextures);
            setProductCountText(newProductTextures.length);
        }
    }, 300));

    clearFiltersEl.addEventListener('click', () => {
        clearAllFilters();
    });

    openFiltersMenuEl.addEventListener('click', () => {
        document.getElementsByClassName('js-filter-selection')[0].classList.remove('hidden');
        document.getElementsByClassName('js-base-product-view')[0].classList.add('hidden');
    });

    filterBackEl.addEventListener('click', () => {
        document.getElementsByClassName('js-filter-selection')[0].classList.add('hidden');
        document.getElementsByClassName('js-base-product-view')[0].classList.remove('hidden');
    });

    patternBackEl.addEventListener('click', () => {
        NavHelper.isPatternContext = false;
        NavHelper.selectNavByName(NavTypes.Product, true);
    });

    [].slice.call(productCloseEls).forEach((el: Element) => {
        el.addEventListener('click', () => {
            NavHelper.closeLeftNav();
            showProductName();
        });
    });

    [].slice.call(document.getElementsByClassName('js-product-option')).forEach((el: Element) => { 
        el.addEventListener('click', () => {
            if (el.classList.contains('selected')) {
                return;
            }

            [].slice.call(document.getElementsByClassName('js-product-option')).forEach((oEl: Element) => { 
                oEl.classList.remove('selected');
            });

            el.classList.add('selected');

            const option: string = el.getAttribute('data-option');
            if (option == 'product') {
                setProductsEls();
            } else if (option == 'model') {
                setModelsEls();
            }
        })
    });

    document.addEventListener('menuclose', (e: CustomEvent) => {
        if (e.detail.navType === NavTypes.Product) {
            showProductName();
        }
    });

    document.addEventListener('menuopen', (e: CustomEvent) => {
        if (e.detail.navType === NavTypes.Product) {
            hideProductName();
        }
    });
};

const setFilterEventListeners = () => {
    if (filterEls && filterEls.length) {
        [].slice.call(filterEls).forEach((el: Element) => {
            el.addEventListener('click', () => {
                if (!el.classList.contains('disabled')) {
                    toggleFilter(el);
                    applyFilters();
                }
            });
        });
    }
};

const setProductEventListeners = () => {
    if (productEls && productEls.length) {
        [].slice.call(productEls).forEach((el: Element) => {
            if (!el.classList.contains('disabled')) {
                el.addEventListener('click', () => {
                    applySelectedProduct(el);
                });

                const mixEl: Element = el.getElementsByClassName('js-mix-icon')[0];
                if (mixEl) {
                    mixEl.addEventListener('click', () => {
                        ColourMixing.setBaseSelectedColour({ productTextures: baseProductTextures, colourEl: el }).then(() => {
                            ColourMixing.openBox();
                        });
                    });
                }
            }

            const infoEl: Element = el.getElementsByClassName('js-info-icon')[0];
            if (infoEl) {
                infoEl.addEventListener('click', async (e: Event) => {
                    e.stopPropagation();
                    
                    try {
                        const matchingProductSynopses: ProductSynopses = productSynopses.find((ps: ProductSynopses) => ps.id === infoEl.getAttribute('data-product-id'));
                        if (matchingProductSynopses) {
                            LoadingHelper.start();
                            await ProductDescription.show(matchingProductSynopses);
                        }
                    } finally {
                        LoadingHelper.stop();
                    }
                });
            }
        });
    }
};

const setProductModelListeners = () => {
    if (productEls && productEls.length) {
        [].slice.call(productEls).forEach((el: Element) => {
            el.addEventListener('click', () => {
                applySelectedModel(el);
            });
        });
    }
};

const setLayingPatternEventListeners = () => {
    if (layingPatternEls && layingPatternEls.length) {
        [].slice.call(layingPatternEls).forEach((el: Element) => {
            el.addEventListener('click', () => {
                applySelectedLayingPattern(el);
            });
        });
    }
};

const setJointingEventListeners = () => {
    if (jointingEls && jointingEls.length) {
        [].slice.call(jointingEls).forEach((el: Element) => {
            el.addEventListener('click', () => {
                applySelectedJointing(el);
            });
        });
    }
};

createBaseEventListeners();

const hideLoading = () => {
    visualiserLoadingEl.classList.add('hidden');
    visualiserEl.removeAttribute('style');
    LoadingHelper.removeBaseStyle();
};

let baseProductTextures: Array<ProductTexture> = null;
let baseModelTextures: Array<ProductModel> = null;
let baseFilters: Array<Filter> = null;
let categoryTypes: Array<CategoryType> = null;
let productSynopses: Array<ProductSynopses> = null;
let productTextures: Array<ProductTexture> = null;
let modelTextures: Array<ProductModel> = null;
let filters: Array<Filter> = null;

const setData = async (isCommercial?: boolean) => {
    return new Promise<any>(async (resolve, reject) => {
        try {
            let response = null;
            LoadingHelper.start();

            if (isCommercial || CatalogueContext.isCommercial()) {
                NavHelper.showCommercialCTAContent();
                response = await VisualisationGatewayService.getCommercialCatalogue();
            } else if (CatalogueContext.isPOS()) {
                response = await VisualisationGatewayService.getPOSCatalogue(CatalogueContext.getCatalogueQSValue());
            } else {
                response = await VisualisationGatewayService.getDomesticCatalogue();
            }

            hideLoading();
            
            if (response == null) {
                console.error('Unable to get data. isCommercial: ', isCommercial);
                reject();
            }

            baseProductTextures = ProductTextureMapper.map(response.data).sort((a, b) => a.name.localeCompare(b.name));
            baseModelTextures = ProductModelMapper.map(response.data).sort((a, b) => a.name.localeCompare(b.name));
            baseFilters = FilterMapper.map(response.data).sort((a, b) => a.key.localeCompare(b.key));
            categoryTypes = CategoryTypesMapper.map(response.data);
            productSynopses = ProductSynopsesMapper.map(response.data);
            productTextures = baseProductTextures;
            modelTextures = baseModelTextures;
            filters = baseFilters;

            NavHelper.setNavColourPalette({
                logoUrl: response.data.logoUrl,
                logoHex: response.data.logoBackgroundHex,
                navBackgroundHex: response.data.navBackgroundHex,
                navSubBackgroundRGBA: response.data.navSubBackgroundRGBA,
                navPrimaryForegroundHex: response.data.navPrimaryForegroundHex,
                navSecondaryForegroundHex: response.data.navSecondaryForegroundHex
            });

            NavHelper.setHomeUrl(response.data.homepageUrl);

            if (CatalogueContext.isPOS()) {
                SplashScreen.setPOSSplashContent({
                    title: response.data.splashScreenTitle,
                    headerImage: response.data.splashScreenHeaderImageUrl,
                    introduction: response.data.splashScreenIntroduction,
                    marshallsLogo: response.data.splashScreenInPartnershipWithLogoUrl,
                    partnershipLogo: response.data.logoUrl
                });
            }
            
            resolve(response);
        } finally {
            LoadingHelper.stop();
        }
    });
};

if (textureKey && !isShareLink) {
    NavHelper.disableProductPageNavs();
    
    await setData();
    
    matchingProductTexture = productTextures.find((pt: ProductTexture) => pt.textureKey == textureKey);

    setLayingPatternEls();
    setJointingEls();

    NavHelper.selectNavByName(NavTypes.Pattern);

    LayingPatternViewer.create({ productTexture: matchingProductTexture, layingPatternId: parseInt(layingPatternEls[0].getAttribute('data-id')), includeDayCycle: !!includeDayCycle });
    applySelectedLayingPattern(layingPatternEls[0], true);
    applySelectedJointing(jointingEls[0], true);
} else if (productId && productId.toLowerCase() !== 'all' && !isShareLink) {
    NavHelper.disableProductPageNavs();

    await setData();

    isSubsetProducts = true;

    productTextures = baseProductTextures.filter((pt: ProductTexture) => pt.productId === productId);
    matchingProductTexture = productTextures[0];
    previousMatchingProductTexture = matchingProductTexture;

    subProductListEl.classList.remove('hidden');
    subProductListEl.parentElement.classList.remove('hidden');

    setProductsEls(subProductListEl);
    setLayingPatternEls();
    setJointingEls();

    NavHelper.selectNavByName(NavTypes.Pattern);

    LayingPatternViewer.create({ productTexture: matchingProductTexture, layingPatternId: parseInt(layingPatternEls[0].getAttribute('data-id')), includeDayCycle: !!includeDayCycle });
    applySelectedProduct(productEls[0], true);
    applySelectedLayingPattern(layingPatternEls[0], true);
    applySelectedJointing(jointingEls[0], true);
} else if (patternId && !isShareLink) {
    NavHelper.disableProductPageNavs();
    await setData();
    LayingPatternViewer.create({ layingPatternId: parseInt(patternId), includeDayCycle: !!includeDayCycle });
} else {
    const setProductEls = () => {
        setFiltersEls();
        setCategoryTypesEls();
        applyCategoryType(categoryTypes.find((ct: CategoryType) => ct.name.toLowerCase().replace(/\s/g, '').includes(productCategoryKey?.toLowerCase()))?.key || categoryTypes[0].key);
        setProductCountText();
    };

    // Check if a catalogue has been specified and set accordingly. If not, let the user choose.
    if (CatalogueContext.isMarshallsCatalogue() || CatalogueContext.isPOS()) {
        if (productId && productId.toLowerCase() === 'all') {
            NavHelper.disableProductPageNavs();
        }

        if (isInFrame()) {
            await setData();
            setProductEls();
            NavHelper.selectNavByName(NavTypes.Product);
            LayingPatternViewer.create({ includeDayCycle: !!includeDayCycle });
        } else {
            const response = await setData();
            const quickPick: any = response.data.quickPicks && response.data.quickPicks.length ? response.data.quickPicks[0] : null;

            setProductEls();
            SplashScreen.show({ showOnlyCommercial: CatalogueContext.isCommercial(), showOnlyDomestic: CatalogueContext.isDomestic(), showOnlyPOS: CatalogueContext.isPOS() });
            SplashScreen.onGetStartedClick(() => NavHelper.selectNavByName(NavTypes.Product));

            if (isShareLink) {
                let productTexture: ProductTexture = Clone.deep(baseProductTextures.find((pt: ProductTexture) => pt.textureKey === textureKey));
                if (!productTexture) {
                    const baseTextureKey: string = textureKey.split('|')[0];
                    productTexture = Clone.deep(baseProductTextures.find((pt: ProductTexture) => pt.textureKey === baseTextureKey));
                }
                
                productTexture.textureKey = textureKey;

                matchingProductTexture = productTexture;
                currentLayingPatternId = parseInt(patternId);
                currentJointingKey = jointKey;

                updateProductName();

                LayingPatternViewer.create({ 
                    productTexture: productTexture,
                    layingPatternId: parseInt(patternId),
                    jointingKey: jointKey,
                    modelUrl: modelUrl,
                    includeDayCycle: !!includeDayCycle
                });
            } else if (quickPick && !CatalogueContext.isCommercial()) {
                let productTexture: ProductTexture = baseProductTextures.find((pt: ProductTexture) => pt.textureKey === quickPick.productTextureKey);

                matchingProductTexture = productTexture;
                currentLayingPatternId = quickPick.patternId;
                currentJointingKey = quickPick.jointTextureKey;

                updateProductName();

                LayingPatternViewer.create({ 
                    productTexture: productTexture,
                    layingPatternId: quickPick.patternId,
                    jointingKey: quickPick.jointTextureKey,
                    includeDayCycle: !!includeDayCycle
                });
            } else {
                LayingPatternViewer.create({ includeDayCycle: !!includeDayCycle });
            }
        }
    } else {
        visualiserLoadingEl.classList.add('hidden');
        visualiserEl.removeAttribute('style');
        LoadingHelper.removeBaseStyle();
        
        SplashScreen.show();
        SplashScreen.onGetStartedClick(async (selectedCatalogue: string) => {
            await setData(selectedCatalogue === 'commercial');
            setProductEls();
            NavHelper.selectNavByName(NavTypes.Product);
        });

        LayingPatternViewer.create({ includeDayCycle: !!includeDayCycle });
    }

    subProductListEl.classList.remove('hidden');
    subProductListEl.parentElement.classList.remove('hidden');
    patternHeaderEl.classList.remove('hidden');
}

if (enableTools) {
    NavHelper.showToolsNav();
    Tools.init();
}

VirtualPlatforms.init();